import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Btn } from '../components/Btn.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { ExpiryChip } from '../components/ExpiryChip.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { instanceOptionLabel } from '../lib/instanceOption.ts';
import type { ComplianceDocRow } from '../lib/companyDetailTabs.ts';
import type { AdminSubmission, CompanyDocument, Question } from '../lib/types.ts';
import { CommentModal } from './CommentModal.tsx';
import { documentRowData } from './documentRow.ts';
import { FileHistoryModal } from './FileHistoryModal.tsx';
import { FilePreviewModal } from './FilePreviewModal.tsx';
import { NotCompliantModal } from './NotCompliantModal.tsx';
import { useReviewMutation } from './useReviewMutation.ts';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// A scannable table of document_upload questions (the Compliance Documents
// tab). Other question types keep the SubmissionRow layout (handled by the
// caller).
export function DocumentReviewTable({
  rows,
  subByQ,
  companyId,
  documents,
  documentName,
  questionById,
}: {
  rows: ComplianceDocRow[];
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  documents: CompanyDocument[];
  documentName: (id: string) => string;
  questionById: Map<string, Question>;
}) {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2">Document</th>
            <th className="px-4 py-2">Category</th>
            <th className="px-4 py-2">Uploaded</th>
            <th className="px-4 py-2">Expiry</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ question, categoryName }) => (
            <DocumentReviewRow
              key={question.id}
              question={question}
              categoryName={categoryName}
              submission={subByQ.get(question.id)}
              companyId={companyId}
              documents={documents}
              documentName={documentName}
              instanceOption={instanceOptionLabel(question, questionById)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentReviewRow({
  question,
  categoryName,
  submission,
  companyId,
  documents,
  documentName,
  instanceOption,
}: {
  question: Question;
  categoryName: string;
  submission: AdminSubmission | undefined;
  companyId: string;
  documents: CompanyDocument[];
  documentName: (id: string) => string;
  instanceOption: string | null;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const reviewMutation = useReviewMutation(companyId, submission?.id, () => {
    setRejectOpen(false);
    setCommentOpen(false);
  });
  function closeReject() {
    setRejectOpen(false);
    reviewMutation.reset();
  }
  function closeComment() {
    setCommentOpen(false);
    reviewMutation.reset();
  }

  const row = documentRowData(submission);
  const files = submission?.submission_files ?? [];
  const status = submission?.status;
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const isNa = status === 'not_applicable';
  const dec = reviewMutation.variables?.decision;
  const busy = reviewMutation.isPending;
  const loadingFor = (target: 'approve' | 'reject' | 'na', active: boolean) =>
    busy && (dec === target || (dec === 'reset' && active));

  const uploaded =
    row.kind === 'file'
      ? formatDate(row.uploadedAt)
      : row.kind === 'adopted' && row.uploadedAt
        ? formatDate(row.uploadedAt)
        : '—';

  return (
    <>
      <tr className="border-t border-slate-100 align-top">
        <td className="px-4 py-3">
          <div className="font-semibold text-slate-900">
            {question.label}
            {instanceOption && (
              <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
                For: {instanceOption}
              </span>
            )}
          </div>
          {submission?.reviewer_comment && (
            <div
              className="mt-1 flex items-start gap-1 text-xs text-orange-700"
              title={submission.reviewer_comment}
            >
              <NavIcon name="message-square" size={12} />
              <span>{submission.reviewer_comment}</span>
            </div>
          )}
          {question.required && (
            <span className="mt-0.5 inline-block rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-600">
              Mandatory
            </span>
          )}
          {row.kind === 'adopted' && (
            <div className="mt-0.5 text-xs text-slate-500">
              Adopted: {row.documentId ? documentName(row.documentId) : 'document'}
            </div>
          )}
          {row.kind === 'awaiting' && (
            <div className="mt-0.5 text-xs italic text-slate-400">Awaiting document</div>
          )}
        </td>
        <td className="px-4 py-3 text-slate-600">{categoryName}</td>
        <td className="px-4 py-3 text-slate-600">{uploaded}</td>
        <td className="px-4 py-3">
          {row.kind === 'file' ? (
            <ExpiryChip expiresAt={row.expiresAt} />
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          <SubmissionStatusBadge status={submission?.status ?? 'pending'} />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-1">
            <Btn
              size="sm"
              variant="secondary"
              aria-label="Document history"
              title="History"
              disabled={!submission}
              onClick={() => setHistoryOpen(true)}
            >
              <NavIcon name="clock" size={16} />
            </Btn>
            <Btn
              size="sm"
              variant="secondary"
              aria-label="View document"
              title="View"
              disabled={row.kind !== 'file'}
              onClick={() => setPreviewOpen(true)}
            >
              <NavIcon name="eye" size={16} />
            </Btn>
            <Btn
              size="sm"
              variant={isApproved ? 'success' : 'secondary'}
              aria-pressed={isApproved}
              aria-label="Compliant"
              title="Compliant"
              loading={loadingFor('approve', isApproved)}
              onClick={() => reviewMutation.mutate({ decision: isApproved ? 'reset' : 'approve' })}
            >
              <NavIcon name="check-circle" size={16} />
            </Btn>
            <Btn
              size="sm"
              variant={isRejected ? 'danger' : 'secondary'}
              aria-pressed={isRejected}
              aria-label="Not compliant"
              title="Not compliant"
              loading={loadingFor('reject', isRejected)}
              onClick={() =>
                isRejected ? reviewMutation.mutate({ decision: 'reset' }) : setRejectOpen(true)
              }
            >
              <NavIcon name="x-circle" size={16} />
            </Btn>
            <Btn
              size="sm"
              variant={isNa ? 'warning' : 'secondary'}
              aria-pressed={isNa}
              aria-label="Not applicable"
              title="N/A"
              loading={loadingFor('na', isNa)}
              onClick={() => reviewMutation.mutate({ decision: isNa ? 'reset' : 'na' })}
            >
              N/A
            </Btn>
            <Btn
              size="sm"
              variant="secondary"
              aria-label={`Add comment for "${question.label}"`}
              title="Comment"
              onClick={() => setCommentOpen(true)}
            >
              <NavIcon name="message-square" size={16} />
            </Btn>
          </div>
        </td>
      </tr>
      {reviewMutation.error && !rejectOpen && !commentOpen && (
        <tr>
          <td colSpan={6} className="px-4 pb-3">
            <ErrorMessage error={reviewMutation.error} />
          </td>
        </tr>
      )}
      {createPortal(
        <>
          {rejectOpen && (
            <NotCompliantModal
              question={question}
              documents={documents}
              pending={reviewMutation.isPending}
              error={reviewMutation.error}
              onClose={closeReject}
              onSubmit={(payload) => reviewMutation.mutate({ decision: 'reject', ...payload })}
            />
          )}
          {commentOpen && (
            <CommentModal
              question={question}
              pending={reviewMutation.isPending}
              error={reviewMutation.variables?.decision === 'comment' ? reviewMutation.error : null}
              onClose={closeComment}
              onSubmit={(comment) => reviewMutation.mutate({ decision: 'comment', comment })}
            />
          )}
          {previewOpen && row.kind === 'file' && submission && (
            <FilePreviewModal
              companyId={companyId}
              submissionId={submission.id}
              files={files}
              startIndex={Math.max(0, files.indexOf(row.primaryFile))}
              onClose={() => setPreviewOpen(false)}
            />
          )}
          {historyOpen && submission && (
            <FileHistoryModal
              companyId={companyId}
              submissionId={submission.id}
              questionLabel={question.label}
              onClose={() => setHistoryOpen(false)}
            />
          )}
        </>,
        document.body,
      )}
    </>
  );
}
